import { useCallback, useRef, useState } from 'react';
import { showAppNotification } from '@rainbow-modules/app';
import Checks from 'components/icons/checks';
import FrequentMessage from 'components/FrequentMessage';
import MicrophoneIcon from 'components/icons/microphone';
import useMultipartMutation from 'data/hooks/useMultipartMutation';
import {
    ModalContainer,
    ModalDescription,
    ModalIconContainer,
    ModalTitle,
    ModalBody,
    NoteInput,
    BoxContainer,
    InputContainer,
    ActionsContainer,
    ActionButton,
    StyledDictateForm,
} from '../styled';

interface Params {
    onNoteChanged?: (value: string) => void;
    agentId: string;
}

const ResolveModal = ({
    onNoteChanged = () => {},
    agentId,
}: Params) => {
    const [value, setValue] = useState('');
    const [isDictating, setIsDictating] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>();

    const {
        isLoading: isTranscribing,
        mutateAsync: transcribeAudio,
    } = useMultipartMutation<FormData, { transcription?: string }>({
        method: 'POST',
        pathname: `/agents/${agentId}/transcribe`,
    });

    const handleNoteChanged = useCallback((inputValue: string) => {
        setValue(inputValue);
        onNoteChanged(inputValue);
    }, [onNoteChanged]);

    const setInputValue = (text: string) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line max-len
        const textarea = inputRef.current.fieldRef.current.textareaRef.current;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype,
            'value',
        ).set;
        nativeInputValueSetter?.call(textarea, text);
        const event = new Event('input', { bubbles: true });
        textarea.dispatchEvent(event);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleDictateAccept = useCallback(async (blob: Blob | null) => {
        setIsDictating(false);
        if (!blob) return;

        try {
            const formData = new FormData();
            formData.append('audio', blob);
            const { transcription } = await transcribeAudio({
                body: formData,
            });
            if (transcription) {
                handleNoteChanged(transcription);
            }
        } catch (error) {
            const message = (error as Error)?.message || 'Something went wrong. Please try again.';
            showAppNotification({
                title: 'Error',
                description: message,
                icon: 'error',
                timeout: 5000,
            });
        }
    }, [handleNoteChanged, transcribeAudio]);

    const handleDictateClick = useCallback(() => setIsDictating(true), []);
    const handleDictateCancel = useCallback(() => setIsDictating(false), []);

    const disableElements = isTranscribing;

    return (
        <ModalContainer>
            <ModalIconContainer>
                <Checks />
            </ModalIconContainer>
            <ModalTitle>Ready to Mark as Solved?</ModalTitle>
            <ModalBody>
                <ModalDescription>
                    Once you confirm, this task will be marked as complete.
                    Any last thoughts? Jot them down in the note area.
                </ModalDescription>
                {isDictating ? (
                    <StyledDictateForm
                        onAccept={handleDictateAccept}
                        onCancel={handleDictateCancel}
                    />
                ) : (
                    <BoxContainer>
                        <InputContainer>
                            <NoteInput
                                variant="shaded"
                                labelAlignment="left"
                                value={value}
                                placeholder="Add your notes here..."
                                rows={1}
                                grow
                                onChange={(event) => handleNoteChanged(event.target.value)}
                                ref={inputRef}
                                disabled={disableElements}
                            />
                            <ActionsContainer>
                                <FrequentMessage
                                    context="notes"
                                    onSelectMessage={setInputValue}
                                    agentId={agentId}
                                    disabled={disableElements}
                                />
                                <ActionButton
                                    tooltip="Dictate"
                                    icon={<MicrophoneIcon />}
                                    onClick={handleDictateClick}
                                    disabled={disableElements}
                                />
                            </ActionsContainer>
                        </InputContainer>
                    </BoxContainer>
                )}
            </ModalBody>
        </ModalContainer>
    );
};

export default ResolveModal;
